import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ArchiveStatusFilter = 'all' | 'active' | 'archived';
export type SortDirection = 'asc' | 'desc';

export type AdminTeamsPageState = {
  filters: {
    nameSearch: string;
    teamLeadFilter: string | null;
    archiveStatus: ArchiveStatusFilter;
  };
  sort: {
    field: 'name';
    direction: SortDirection;
  };
  // Create-Team modal (UC-56). Draft lives in React Hook Form; the slice
  // holds cross-render modal state (open flag, submit progress, server error).
  createTeamModalOpen: boolean;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminTeamsPageState = {
  filters: {
    nameSearch: '',
    teamLeadFilter: null,
    archiveStatus: 'all',
  },
  sort: {
    field: 'name',
    direction: 'asc',
  },
  createTeamModalOpen: false,
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminTeamsPageSlice = createSlice({
  name: 'adminTeamsPage',
  initialState,
  reducers: {
    setNameSearch(state, action: PayloadAction<string>) {
      state.filters.nameSearch = action.payload;
    },
    setTeamLeadFilter(state, action: PayloadAction<string | null>) {
      state.filters.teamLeadFilter = action.payload;
    },
    setArchiveStatus(state, action: PayloadAction<ArchiveStatusFilter>) {
      state.filters.archiveStatus = action.payload;
    },
    toggleNameSort(state) {
      state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc';
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },
    openCreateTeamModal(state) {
      state.createTeamModalOpen = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeCreateTeamModal(state) {
      state.createTeamModalOpen = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
  },
});

export const {
  setNameSearch,
  setTeamLeadFilter,
  setArchiveStatus,
  toggleNameSort,
  clearFilters,
  openCreateTeamModal,
  closeCreateTeamModal,
  setSubmitInProgress,
  setLastErrorKey,
} = adminTeamsPageSlice.actions;

type AppRootState = {
  adminTeamsPage: AdminTeamsPageState;
};

export const selectAdminTeamsPage = (state: AppRootState) =>
  state.adminTeamsPage;
